import { Button, Card, Modal, useToast } from '@citizen-walk/shared-ui';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { DistrictEditForm } from './DistrictEditForm';
import { importDistrictFromConfig, parseDistrictImport } from './districtJsonIo';
import { useDistricts } from './useDistricts';

/**
 * 地区一覧ページ (Phase 5.3 R-33 step 3 → admin 第 10 弾 で CRUD 追加)。
 *
 * 旧 apps/admin/src/pages/DistrictsPage.tsx 踏襲:
 *   - master role のみ「新規作成」可能 (非 master は read-only)
 *   - 一覧は displayName 昇順、archived は trailing chip 表示
 *   - 詳細リンクで個別 page へ遷移、個別 page で編集 / archive
 *
 * 認可: master role なら全地区 read 可。district admin は accessibleDistricts のみ read。
 */
export function DistrictsAdminPage() {
  const state = useDistricts();
  const { state: authState } = useAdminAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const isMaster = authState.role === 'master';
  const uid = authState.user?.uid ?? '';
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);

  /**
   * 地区設定 JSON を取込み、各 config から新規地区を作成 (新 id + 新 urlToken)。
   * staging→本番の移送用。参照 master (M1〜M5) は事前取込が前提。
   */
  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast('error', 'ファイルサイズが大きすぎます (上限 5MB)');
      return;
    }
    if (!uid) {
      toast('error', 'サインインが必要です');
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      if (!text.trim()) throw new Error('ファイルが空です');
      const raw = JSON.parse(text) as unknown;
      const { items, errors } = parseDistrictImport(raw);
      if (items.length === 0) {
        toast('error', `取込可能な地区設定がありません (失敗 ${errors.length} 件)`);
        return;
      }
      const msg =
        `${items.length} 件の地区を新規作成します (新しい id と urlToken が発行されます)。\n` +
        '※ 参照する AI 解析マスタ (M1〜M5) は事前に本番へ取込んでおいてください。\n' +
        (errors.length > 0 ? `検証失敗 ${errors.length} 件は skip。\n` : '') +
        '続行しますか?';
      if (!confirm(msg)) return;
      let ok = 0;
      for (const cfg of items) {
        await importDistrictFromConfig(cfg, uid);
        ok += 1;
      }
      toast('success', `${ok} 件の地区を作成しました`);
    } catch (err) {
      toast('error', `取込失敗: ${(err as Error).message}`);
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">地区管理</h1>
          <p className="text-sm text-slate-500 mt-1">
            地区の作成・編集・アーカイブ（非表示）・オフライン地図の生成。
          </p>
        </div>
        {isMaster && (
          <div className="flex gap-2 flex-wrap shrink-0">
            <Button onClick={() => setCreateOpen(true)} size="md">
              ＋ 新規作成
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing || !uid}
              aria-label="地区設定を JSON で取込"
            >
              {importing ? '取込中…' : '📤 地区設定を取込'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => void handleImportFile(e)}
              aria-hidden="true"
            />
          </div>
        )}
      </header>

      {state.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {state.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {state.error.message}</p>
          <p className="mt-2 text-xs text-slate-500">
            master role 未付与の可能性があります。emulator の seed script で{' '}
            <code>role: master</code> を付与してください。
          </p>
        </Card>
      )}

      {state.status === 'ready' && state.items.length === 0 && (
        <Card padding="md">
          <p className="text-sm text-slate-600">
            地区が登録されていません。
            {isMaster ? '上の「＋ 新規作成」から作成してください。' : '権限がありません。'}
          </p>
        </Card>
      )}

      {state.status === 'ready' && state.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2">
          {state.items.map((d) => {
            const archived = !!d.archivedAt;
            return (
              <li key={d.id}>
                <Card
                  padding="md"
                  className={`flex items-center justify-between gap-3 ${archived ? 'opacity-60' : ''}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">
                      {d.name}
                      {archived && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 align-middle">
                          アーカイブ済
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      モード: <code>{d.mode}</code> / 参加URLトークン: <code>{d.urlToken}</code>
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      PMTiles:{' '}
                      {d.mapPmtiles ? (
                        <span className="text-success-600">
                          生成済 ({(d.mapPmtiles.sizeBytes / 1024 / 1024).toFixed(1)} MB, zoom=
                          {d.mapPmtiles.maxZoom})
                        </span>
                      ) : (
                        <span className="text-slate-400">未生成</span>
                      )}
                    </p>
                  </div>
                  <Link
                    to={`/districts/${d.id}`}
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline shrink-0"
                  >
                    詳細 →
                  </Link>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="地区を新規作成"
        size="lg"
        closeOnOverlayClick={false}
      >
        {uid ? (
          <DistrictEditForm
            target={null}
            uid={uid}
            onSaved={() => setCreateOpen(false)}
            onCancel={() => setCreateOpen(false)}
          />
        ) : (
          <p className="text-sm text-danger-700">サインインが必要です。</p>
        )}
      </Modal>
    </>
  );
}
